import { LDateTime } from '@app/components/date/LDateTime';
import { auditReports } from '@mi-front/api-clients';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

interface Props {
  run: auditReports.AodRun;
}

function isOlafMissingSpotMediaDto(
  item:
    | auditReports.OlafFailedDataDto
    | auditReports.OlafMissingSpotMediaDto
    | auditReports.OlafMissingSegmentsDto,
): item is auditReports.OlafMissingSpotMediaDto {
  return (
    'type' in item &&
    item.type === auditReports.OlafMissingSpotMediaDtoTypeEnum.MISSING_SPOT_MEDIA &&
    'customId' in item
  );
}

function isOlafMissingSegmentsDto(
  item:
    | auditReports.OlafFailedDataDto
    | auditReports.OlafMissingSpotMediaDto
    | auditReports.OlafMissingSegmentsDto,
): item is auditReports.OlafMissingSegmentsDto {
  return (
    'type' in item &&
    item.type === auditReports.OlafMissingSegmentsDtoTypeEnum.MISSING_SEGMENTS &&
    'datetime' in item
  );
}

export function ReprocessErrorInfo({ run }: Props) {
  const { $t } = useIntl();

  const shouldShowError =
    run.status !== auditReports.AodRunStatusEnum.COMPLETED_WITH_EXPORT && run.isReprocessFailed;

  const failedItems =
    run.statusData?.userReprocess?.failedItems || run.statusData?.automaticReprocess?.failedItems;

  const missingSpotMediaItems = failedItems?.filter(isOlafMissingSpotMediaDto) || [];
  const missingSegmentsItems = failedItems?.filter(isOlafMissingSegmentsDto) || [];

  const hasFailedItems = missingSpotMediaItems.length > 0 || missingSegmentsItems.length > 0;

  if (!shouldShowError || !hasFailedItems) {
    return null;
  }

  return (
    <Alert severity="error">
      <AlertTitle>
        {$t({
          id: 'audit.aod.actions.triggerReprocess.failed.title',
          defaultMessage: 'Error en el último reprocesamiento',
        })}
      </AlertTitle>
      <Box display="flex" flexDirection="column" gap={1} mt={1}>
        {$t({
          id: 'audit.aod.actions.triggerReprocess.failed.message',
          defaultMessage: 'Hay problemas que deben resolverse con el reprocesamiento:',
        })}
        {missingSpotMediaItems.length > 0 && (
          <Box>
            <Typography variant="subtitle2" my={1}>
              {$t({
                id: 'audit.aod.actions.triggerReprocess.failed.missingSpotMedia',
                defaultMessage: 'Medios de spot faltantes:',
              })}
            </Typography>
            <ul>
              {missingSpotMediaItems.map((item, index) => (
                <li key={index}>
                  <Typography component="span" variant="body2">
                    {item.customId}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
        {missingSegmentsItems.length > 0 && (
          <Box>
            <Typography variant="subtitle2" my={1}>
              {$t({
                id: 'audit.aod.actions.triggerReprocess.failed.missingSegments',
                defaultMessage: 'Segmentos faltantes:',
              })}
            </Typography>
            <ul>
              {missingSegmentsItems.map((item, index) => (
                <li key={index}>
                  <Typography component="span" variant="body2">
                    <LDateTime value={item.datetime} showSeconds />
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
      </Box>
    </Alert>
  );
}
